import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { ConfigOptions, ConfigService } from '@wilson/config';
import {
  Project,
  ProjectCreatePayload,
  ProjectMany,
  ProjectUpdatePayload,
  ProjectsOptions,
  Agreement,
  ProjectKPIs,
} from '@wilson/interfaces';
import { stringify } from 'qs';
import { Observable, map } from 'rxjs';

interface DeleteProjectAgreementResponse {
  projectId: string;
  agreementIds: string[];
  unlinkedCount: number;
}

interface LinkAgreementToProjectResponse {
  projectId: string;
  agreementIds: string[];
}

@Injectable({
  providedIn: 'root',
})
export class ProjectsGateway {
  protected readonly http = inject(HttpClient);
  protected readonly config = inject<ConfigOptions>(ConfigService);

  protected readonly path = 'projects';

  getProjects(options?: ProjectsOptions): Observable<ProjectMany> {
    const params = stringify(options, {
      arrayFormat: 'brackets',
    });
    return this.http.get<ProjectMany>(
      `${this.config.host}/${this.path}?${params}`,
    );
  }

  getProjectById(id: string, relations?: string[]): Observable<Project> {
    const options: ProjectsOptions = {
      where: {
        id: id,
      },
      relations,
    };
    return this.getProjects(options).pipe(map((projects) => projects.data[0]));
  }

  createProject(payload: ProjectCreatePayload): Observable<Project> {
    return this.http.post<Project>(`${this.config.host}/${this.path}`, payload);
  }

  deleteOne(id: string): Observable<Project> {
    return this.http.delete<Project>(`${this.config.host}/${this.path}/${id}`);
  }

  updateProject(
    id: string,
    payload: ProjectUpdatePayload,
  ): Observable<Project> {
    return this.http.patch<Project>(
      `${this.config.host}/${this.path}/${id}`,
      payload,
    );
  }

  getProjectAgreements(projectId: string): Observable<Agreement[]> {
    return this.http.get<Agreement[]>(
      `${this.config.host}/${this.path}/${projectId}/project-agreements`,
    );
  }

  linkAgreementsToProject(
    projectId: string,
    payload: { agreementIds: (string | undefined)[] },
  ): Observable<LinkAgreementToProjectResponse> {
    return this.http.patch<LinkAgreementToProjectResponse>(
      `${this.config.host}/${this.path}/${projectId}/link-agreements`,
      payload,
    );
  }

  unlinkAgreementsFromProject(
    projectId: string,
    payload: (string | undefined)[],
  ): Observable<DeleteProjectAgreementResponse> {
    return this.http.delete<DeleteProjectAgreementResponse>(
      `${this.config.host}/${this.path}/${projectId}/agreements`,
      { body: { agreementIds: payload } },
    );
  }

  getProjectsKpis(projectIds: string[]): Observable<ProjectKPIs> {
    return this.http.post<ProjectKPIs>(
      `${this.config.host}/${this.path}/kpis`,
      { projectIds },
    );
  }
}
